import { useState } from "react";
import { NavLink, useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { authApi } from "@/api";
import { releases } from "@/data/releases";
import {
  LayoutDashboard,
  MessageSquare,
  MessagesSquare,
  FileText,
  Send,
  UserX,
  Smartphone,
  Users,
  Settings,
  LogOut,
  Megaphone,
  X,
} from "lucide-react";

const navItems = [
  { to: "/", icon: LayoutDashboard, label: "ダッシュボード" },
  { to: "/messages", icon: MessageSquare, label: "メッセージ" },
  { to: "/conversations", icon: MessagesSquare, label: "会話" },
  { to: "/templates", icon: FileText, label: "テンプレート" },
  { to: "/bulk-send", icon: Send, label: "一斉送信" },
  { to: "/staff", icon: Users, label: "担当者" },
  { to: "/opt-outs", icon: UserX, label: "配信停止" },
  { to: "/devices", icon: Smartphone, label: "端末" },
];

const adminItems = [
  { to: "/settings", icon: Settings, label: "設定" },
];

const SEEN_VERSION_KEY = "sms_seen_version";
const latestVersion = releases[0]?.version ?? "";

export default function Sidebar() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: me } = useQuery({
    queryKey: ["auth-me"],
    queryFn: () => authApi.me(),
  });

  const isAdmin = me?.isAdmin === true;
  const visibleItems = isAdmin ? [...navItems, ...adminItems] : navItems;

  const [showReleaseNote, setShowReleaseNote] = useState(false);
  const seenVersion = localStorage.getItem(SEEN_VERSION_KEY) ?? "";
  const hasUnread = latestVersion !== seenVersion;

  const handleOpenRelease = () => {
    setShowReleaseNote(true);
    localStorage.setItem(SEEN_VERSION_KEY, latestVersion);
  };

  const handleLogout = async () => {
    await authApi.logout().catch(() => {});
    queryClient.clear();
    navigate("/login");
  };

  return (
    <>
      <aside className="w-56 bg-slate-900 text-white min-h-screen flex flex-col">
        <div className="p-4 border-b border-slate-700">
          <h1 className="text-lg font-bold">SMS Gateway</h1>
          <p className="text-xs text-slate-400">管理画面</p>
        </div>
        <nav className="flex-1 p-2 space-y-1">
          {visibleItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-slate-700 text-white"
                    : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`
              }
            >
              <Icon size={18} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-700 space-y-2">
          <button
            onClick={handleOpenRelease}
            className="flex items-center gap-2 w-full px-3 py-1.5 rounded-md text-xs text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <Megaphone size={14} />
            <span>v{latestVersion}</span>
            {hasUnread && (
              <span className="ml-auto w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
            )}
          </button>
          {me && (
            <p className="text-xs text-slate-400 truncate">{me.staffName ?? me.username}</p>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <LogOut size={18} />
            ログアウト
          </button>
        </div>
      </aside>

      {showReleaseNote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-bold text-sm">更新情報</h3>
              <button onClick={() => setShowReleaseNote(false)} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 max-h-96 overflow-y-auto space-y-4">
              {releases.map((r) => (
                <div key={r.version}>
                  <p className="text-xs font-bold text-slate-700 mb-1">v{r.version} <span className="font-normal text-slate-400">({r.date})</span></p>
                  <ul className="text-xs text-slate-600 space-y-1">
                    {r.items.map((item, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="text-blue-400 mt-0.5">&#8226;</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
